import { useRef } from 'react';

import { PROFILE_REQUOTE_HEADER } from '@rover/react-lib/src/apis/personSitterProfile.api';
import { useApiPersonFullSitterProfileRetrieve } from '@rover/rsdk/src/apiClient/latest/api-person-full-sitter-profile-retrieve';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import { HTTP } from '@rover/rsdk/src/modules/Network/orvalHTTPInterface';

import { getFullSitterProfileParams } from '../utils/fullSitterProfileParams';

import { SERVICE_TYPE_PARAM } from './useUpdateSelectedService';

type FullSitterProfile = {
  sitterInfo: EnhancedPersonSitterProfile | undefined;
  isRequoting: boolean;
};

/**
 * Loads the profile quoted for the stay in the URL. Every request after the sitter's first
 * load is a re-quote, and the last profile loaded stays on screen until a new quote arrives.
 */
export const useFullSitterProfile = (
  slug: string,
  searchParams: URLSearchParams
): FullSitterProfile => {
  // The endpoint reads `service` only for the legacy layout's map, so a service picked on the
  // page keeps the one the sitter loaded with instead of re-requesting an identical quote.
  const loadedService = useRef<{ slug: string; service: string | null } | undefined>(undefined);
  if (loadedService.current?.slug !== slug) {
    loadedService.current = { slug, service: searchParams.get(SERVICE_TYPE_PARAM) };
  }
  const requestParams = new URLSearchParams(searchParams);
  if (loadedService.current.service) {
    requestParams.set(SERVICE_TYPE_PARAM, loadedService.current.service);
  } else {
    requestParams.delete(SERVICE_TYPE_PARAM);
  }
  const params = getFullSitterProfileParams(slug, requestParams);
  // The last profile loaded, which stands in for this sitter while a re-quote loads or fails.
  const loaded = useRef<{ slug: string; data: EnhancedPersonSitterProfile } | undefined>(undefined);
  const isRequote = loaded.current?.slug === slug;

  const { data, isPreviousData, isFetching } = useApiPersonFullSitterProfileRetrieve(params, {
    query: {
      keepPreviousData: true,
      queryFn: ({ signal }) =>
        HTTP<EnhancedPersonSitterProfile>({
          url: `/api/v7/people/full-sitter-profile/`,
          method: 'GET',
          params,
          signal,
          ...(isRequote ? { headers: { [PROFILE_REQUOTE_HEADER]: '1' } } : {}),
        }),
    },
  });

  const current = data && !isPreviousData ? data : undefined;
  if (current) loaded.current = { slug, data: current };
  const standIn = loaded.current?.slug === slug ? loaded.current.data : undefined;

  return {
    sitterInfo: current ?? standIn,
    // keepPreviousData only carries over a successful query, so a fetch after a failed
    // re-quote reports no previous data while the stand-in is still on screen.
    isRequoting: !current && !!standIn && isFetching,
  };
};
